import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './DeliveryHeader.css';

/**
 * Delivery Portal header. Modeled on MillHeader's structure, but uses the admin
 * AuthContext (useAuth) rather than a separate PWA auth context — the Delivery
 * Portal is part of the admin app, not a standalone PWA like Mill/Store.
 *
 * ONE list drives both the desktop nav and the phone menu. They used to be two
 * hand-written copies, and the phone copy stopped at Leads - Routes and Catalog
 * never reached it, so a route could not be built or dispatched from a phone.
 */
const NAV_ITEMS: { to: string; label: string }[] = [
  { to: '/delivery/driver', label: 'My route' },
  { to: '/delivery/dashboard', label: 'Dashboard' },
  { to: '/delivery/activation', label: 'Activation' },
  { to: '/delivery/landing', label: 'Landing page' },
  { to: '/delivery/orders', label: 'Upcoming orders' },
  { to: '/delivery/procurement', label: 'Procurement' },
  { to: '/delivery/walk', label: 'Property walk' },
  { to: '/delivery/leads', label: 'Leads' },
  { to: '/delivery/pet-mapping', label: 'Pet Mapping' },
  { to: '/delivery/routes', label: 'Routes' },
  { to: '/delivery/catalog', label: 'Catalog' },
];

const DeliveryHeader: React.FC = () => {
  const { user, logout, canAccessAdminPortal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNavClick = () => setMobileMenuOpen(false);

  const navLinks = (className: string) =>
    NAV_ITEMS.map((item) => (
      <Link
        key={item.to}
        to={item.to}
        className={`${className} ${isActive(item.to) ? 'active' : ''}`}
        onClick={handleNavClick}
      >
        {item.label}
      </Link>
    ));

  return (
    <header className="delivery-header">
      <div className="delivery-header-container">
        <div className="delivery-header-left">
          <div className="delivery-header-branding">
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="delivery-header-logo" />
            <div className="delivery-header-brand-text">
              <div className="delivery-header-company">Red Bridge Farm Feed Co.</div>
              <div className="delivery-header-portal-label">Delivery Portal</div>
            </div>
          </div>
          {user && (
            <nav className={`delivery-header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {navLinks('delivery-nav-link')}
            </nav>
          )}
        </div>
        {user && (
          <div className="delivery-header-right">
            {canAccessAdminPortal && (
              <button
                onClick={() => navigate('/dashboard')}
                className="delivery-btn-portal-switch"
              >
                Admin Portal
              </button>
            )}
            <div className="delivery-header-user-info">
              <div className="delivery-header-username">
                {user.first_name ? `${user.first_name} ${user.last_name}` : user.username}
              </div>
            </div>
            <button onClick={handleLogout} className="delivery-btn-logout">
              Logout
            </button>
            <button
              className={`delivery-hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <span className="delivery-hamburger-line"></span>
              <span className="delivery-hamburger-line"></span>
              <span className="delivery-hamburger-line"></span>
            </button>
          </div>
        )}
        {user && mobileMenuOpen && (
          <div className="delivery-mobile-menu">
            <nav className="delivery-mobile-nav">
              {navLinks('delivery-mobile-nav-link')}
            </nav>
            {canAccessAdminPortal && (
              <>
                <div className="delivery-mobile-divider"></div>
                <div className="delivery-mobile-portal-section">
                  <div className="delivery-mobile-section-label">Switch Portal</div>
                  <a href="/dashboard" className="delivery-mobile-portal-link" onClick={handleNavClick}>
                    Admin Portal
                  </a>
                </div>
              </>
            )}
            <div className="delivery-mobile-divider"></div>
            <button onClick={handleLogout} className="delivery-mobile-logout-btn">
              Logout
            </button>
          </div>
        )}
      </div>
      {mobileMenuOpen && (
        <div className="delivery-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
    </header>
  );
};

export default DeliveryHeader;
